import logger from '@/utils/logger';
/**
 * API HEADERS - Helper pour ajouter automatiquement le JWT
 * 
 * Récupère le token JWT Supabase et ajoute le header Authorization
 * à chaque requête API vers le backend.
 * 
 * SÉCURITÉ: Garantit que TOUTES les requêtes incluent le JWT
 */

import { supabase } from '../lib/supabase';

/**
 * Obtenir les headers HTTP avec Authorization (JWT)
 * 
 * @param includeContentType - Inclure Content-Type: application/json (défaut: true)
 * @returns Headers avec Authorization: Bearer <token>
 * @throws Error si pas de session active
 * 
 * @example
 * const headers = await getAuthHeaders();
 * fetch('/api/data', { headers });
 * 
 * @example FormData upload (sans Content-Type)
 * const headers = await getAuthHeaders(false);
 * fetch('/api/upload', { method: 'POST', headers, body: formData });
 */
export async function getAuthHeaders(includeContentType: boolean = true): Promise<Record<string, string>> {
 const { data: { session }, error } = await supabase.auth.getSession();
 
 if (error || !session) {
 throw new Error('Session non trouvée - utilisateur non connecté');
 }
 
 const headers: Record<string, string> = {
 'Authorization': `Bearer ${session.access_token}`
 };
 
 if (includeContentType) {
 headers['Content-Type'] = 'application/json';
 }
 
 return headers;
}

/**
 * Obtenir les headers HTTP (sans Authorization si non connecté)
 * 
 * @returns Headers avec Authorization si connecté, sinon headers basiques
 * 
 * @example
 * const headers = await getAuthHeadersSafe();
 * fetch('/api/public-data', { headers });
 */
export async function getAuthHeadersSafe(): Promise<Record<string, string>> {
 try {
 return await getAuthHeaders();
 } catch (error) {
 // Si pas connecté, retourner headers basiques
 return {
 'Content-Type': 'application/json'
 };
 }
}

/**
 * Faire une requête fetch avec authentification automatique + retry logic
 * 
 * @param url URL de l'API
 * @param options Options fetch (method, body, etc.)
 * @param retries Nombre de retries (défaut: 2)
 * @returns Promise<Response>
 * 
 * @example
 * const response = await authFetch('/api/data', { method: 'POST', body: JSON.stringify({...}) });
 * 
 * SÉCURITÉ:
 * - Auto-retry en cas de JWT expiré (refreshSession + retry)
 * - Exponential backoff pour éviter surcharge
 * - Timeout configurable
 */
export async function authFetch(
  url: string, 
  options: RequestInit = {}, 
  retries: number = 2
): Promise<Response> {
  const maxRetries = retries;
  let lastError: Error | null = null;
 
  // Vérifier que l'URL est valide
  if (!url || (!url.startsWith('http://') && !url.startsWith('https://'))) {
    logger.error(`[authFetch] URL invalide: ${url}`);
    throw new Error(`URL invalide: ${url}. L'URL doit commencer par http:// ou https://`);
  }

  logger.log(`[authFetch] Requête vers: ${url}`);
 
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Récupérer le JWT à chaque tentative (peut avoir été refresh)
      const headers = await getAuthHeaders();
 
      logger.log(`[authFetch] Tentative ${attempt + 1}/${maxRetries + 1} - Headers:`, {
        hasAuth: !!headers.Authorization,
        url: url.substring(0, 50) + '...'
      });

      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...(options.headers || {})
        }
      });
 
 // Si 401 Unauthorized, tenter refresh JWT + retry
 if (response.status === 401 && attempt < maxRetries) {
 logger.warn(` [authFetch] 401 Unauthorized, tentative refresh JWT (attempt ${attempt + 1}/${maxRetries})`);
 
 // Forcer refresh de la session
 const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
 
 if (refreshError || !session) {
 logger.error(' [authFetch] Impossible de refresh JWT:', refreshError);
 throw new Error('Session expirée - reconnexion requise');
 }
 
 logger.log(' [authFetch] JWT refreshed, retry request');
 
 // Retry avec exponentiel backoff (100ms, 200ms, 400ms...)
 await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(2, attempt)));
 continue; // Retry
 }
 
 // Si 429 Too Many Requests, attendre + retry
 if (response.status === 429 && attempt < maxRetries) {
 const retryAfter = parseInt(response.headers.get('Retry-After') || '1', 10);
 logger.warn(` [authFetch] 429 Rate Limit, attente ${retryAfter}s avant retry`);
 await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
 continue;
 }
 
 // Si 5xx Server Error, retry avec backoff
 if (response.status >= 500 && response.status < 600 && attempt < maxRetries) {
 logger.warn(` [authFetch] ${response.status} Server Error, retry (attempt ${attempt + 1}/${maxRetries})`);
 await new Promise(resolve => setTimeout(resolve, 200 * Math.pow(2, attempt)));
 continue;
 }
 
 // Sinon, retourner la réponse (succès ou erreur client non-retriable)
 return response;
 
 } catch (error: any) {
 lastError = error;
 
 // Network error, timeout, etc. - retry avec backoff
 if (attempt < maxRetries) {
 logger.warn(` [authFetch] Network error, retry (attempt ${attempt + 1}/${maxRetries}):`, error.message);
 await new Promise(resolve => setTimeout(resolve, 200 * Math.pow(2, attempt)));
 continue;
 }
 
 // Dernier retry échoué, throw
 throw error;
 }
 }
 
 // Si on arrive ici, tous les retries ont échoué
 throw lastError || new Error('authFetch failed after all retries');
}

export default {
 getAuthHeaders,
 getAuthHeadersSafe,
 authFetch
};



